import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import {
  MapPin, Clock, User, Truck, Phone, AlertTriangle, ChevronRight,
  CheckCircle, XCircle, Eye, Calendar, Plane, Zap
} from "lucide-react";
import AutoAssignDialog from "./AutoAssignDialog";

const FLAG_LABELS = {
  upcoming_soon: { label: "Starting Soon", color: "bg-red-500/15 text-red-400 border-red-500/20", icon: Clock },
  unconfirmed: { label: "Unconfirmed", color: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20", icon: AlertTriangle },
  no_driver: { label: "No Driver", color: "bg-orange-500/15 text-orange-400 border-orange-500/20", icon: Truck },
  driver_rejected: { label: "Driver Rejected", color: "bg-red-500/15 text-red-400 border-red-500/20", icon: XCircle },
  missing_contact: { label: "Missing Contact", color: "bg-purple-500/15 text-purple-400 border-purple-500/20", icon: Phone },
  unpaid: { label: "Unpaid", color: "bg-blue-500/15 text-blue-400 border-blue-500/20", icon: AlertTriangle },
  awaiting_acceptance: { label: "Awaiting Driver", color: "bg-[#C8A95B]/15 text-[#C8A95B] border-[#C8A95B]/20", icon: Clock },
};

const STATUS_COLORS = {
  pending: { bar: "bg-yellow-500", bg: "bg-yellow-500/10", text: "text-yellow-500" },
  confirmed: { bar: "bg-blue-500", bg: "bg-blue-500/10", text: "text-blue-500" },
  dispatched: { bar: "bg-purple-500", bg: "bg-purple-500/10", text: "text-purple-500" },
  in_progress: { bar: "bg-cyan-500", bg: "bg-cyan-500/10", text: "text-cyan-500" },
  completed: { bar: "bg-green-500", bg: "bg-green-500/10", text: "text-green-500" },
  cancelled: { bar: "bg-red-500", bg: "bg-red-500/10", text: "text-red-500" },
};

const DRIVER_STATUS_LABELS = {
  en_route: "On the Way",
  arrived: "Arrived",
  trip_started: "Passenger On Board",
  completed: "Dropped Off",
};

const DispatchBoard = ({ bookings, drivers, attentionFlags, onRefresh }) => {
  const navigate = useNavigate();
  const [timeView, setTimeView] = useState("today");
  const [autoAssignBooking, setAutoAssignBooking] = useState(null);

  const now = new Date();
  const todayStr = now.toDateString();

  const filteredBookings = useMemo(() => {
    let filtered = bookings.filter(b => b.status !== "completed" && b.status !== "cancelled");
    if (timeView === "today") {
      filtered = filtered.filter(b => new Date(b.pickup_datetime).toDateString() === todayStr);
    } else if (timeView === "tomorrow") {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      filtered = filtered.filter(b => new Date(b.pickup_datetime).toDateString() === tomorrow.toDateString());
    } else if (timeView === "week") {
      const weekEnd = new Date(now);
      weekEnd.setDate(weekEnd.getDate() + 7);
      filtered = filtered.filter(b => new Date(b.pickup_datetime) <= weekEnd);
    }
    return filtered.sort((a, b) => new Date(a.pickup_datetime) - new Date(b.pickup_datetime));
  }, [bookings, timeView, todayStr]);

  const handleDispatch = async (bookingId, driverId) => {
    try {
      await api.post(`/bookings/${bookingId}/dispatch`, { driver_id: driverId, send_to_calendar: false });
      toast.success("Driver assigned");
      onRefresh();
    } catch (error) { toast.error("Failed to dispatch"); }
  };

  const handleConfirm = async (bookingId) => {
    try {
      await api.post(`/bookings/${bookingId}/confirm`);
      toast.success("Booking confirmed");
      onRefresh();
    } catch (error) { toast.error("Failed to confirm"); }
  };

  const getBookingFlags = (bookingId) => {
    const flag = attentionFlags.find(f => f.booking_id === bookingId);
    return flag?.flags || [];
  };

  const isUpcomingSoon = (dateStr) => {
    const dt = new Date(dateStr);
    const diff = dt - now;
    return diff > 0 && diff < 3600000;
  };

  return (
    <div className="space-y-6" data-testid="dispatch-board">
      {/* Attention Flags Summary */}
      {attentionFlags.length > 0 && (
        <div className="p-4 rounded-xl bg-red-500/5 border border-red-500/15">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-red-400" />
            <h3 className="text-sm font-semibold text-red-400">Attention Required ({attentionFlags.length})</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {attentionFlags.slice(0, 8).map((flag, i) => (
              <button key={i} onClick={() => navigate(`/booking/${flag.booking_id}`)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] border border-black/[0.06] transition-colors text-xs"
                data-testid={`attention-flag-${flag.booking_id}`}>
                <span className="font-mono text-[#C8A95B]">{flag.booking_ref}</span>
                <span className="text-muted-foreground">{flag.passenger_name}</span>
                {flag.flags.map(f => {
                  const cfg = FLAG_LABELS[f];
                  return cfg ? <Badge key={f} className={`${cfg.color} border text-[10px]`}>{cfg.label}</Badge> : null;
                })}
              </button>
            ))}
            {attentionFlags.length > 8 && <span className="text-xs text-muted-foreground self-center">+{attentionFlags.length - 8} more</span>}
          </div>
        </div>
      )}

      {/* Time Filter */}
      <div className="flex items-center gap-2">
        {[
          { id: "today", label: "Today" },
          { id: "tomorrow", label: "Tomorrow" },
          { id: "week", label: "This Week" },
          { id: "all", label: "All Active" },
        ].map(t => (
          <button key={t.id} onClick={() => setTimeView(t.id)}
            className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${
              timeView === t.id ? "bg-[#C8A95B] text-black" : "bg-muted/50 text-muted-foreground hover:bg-muted"
            }`}
            data-testid={`dispatch-filter-${t.id}`}>
            {t.label}
          </button>
        ))}
        <div className="flex-1" />
        <span className="text-sm text-muted-foreground">{filteredBookings.length} trip{filteredBookings.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Trip List */}
      {filteredBookings.length === 0 ? (
        <div className="text-center py-16">
          <Calendar className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-muted-foreground">No active trips for {timeView === "all" ? "dispatch" : timeView}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredBookings.map(booking => {
            const flags = getBookingFlags(booking.id);
            const sc = STATUS_COLORS[booking.status] || STATUS_COLORS.pending;
            const upcoming = isUpcomingSoon(booking.pickup_datetime);
            
            return (
              <div key={booking.id} className={`rounded-xl border overflow-hidden transition-all hover:shadow-md ${
                upcoming ? "border-red-500/30 bg-red-500/[0.02]" : "border-black/[0.06]"
              }`} data-testid={`dispatch-trip-${booking.id}`}>
                {/* Status color bar */}
                <div className={`h-1 ${sc.bar}`} />
                
                <div className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    {/* Time */}
                    <div className="lg:w-24 flex-shrink-0">
                      <p className="text-lg font-bold">{new Date(booking.pickup_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                      <p className="text-xs text-muted-foreground">{new Date(booking.pickup_datetime).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                    </div>

                    {/* Main Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <span className="font-mono text-[#C8A95B] font-medium text-sm">{booking.booking_ref}</span>
                        <Badge className={`${sc.bg} ${sc.text} capitalize border-0 text-[11px]`}>{booking.status.replace("_", " ")}</Badge>
                        {booking.driver_status && DRIVER_STATUS_LABELS[booking.driver_status] && (
                          <Badge className="bg-cyan-500/10 text-cyan-500 border-0 text-[11px]">
                            {DRIVER_STATUS_LABELS[booking.driver_status]}
                          </Badge>
                        )}
                        {booking.driver_accepted && (
                          <Badge className="bg-green-500/10 text-green-500 border-0 text-[11px]">Driver Accepted</Badge>
                        )}
                        {flags.map(f => {
                          const cfg = FLAG_LABELS[f];
                          return cfg ? <Badge key={f} className={`${cfg.color} border text-[10px]`}>{cfg.label}</Badge> : null;
                        })}
                      </div>
                      
                      <div className="flex items-center gap-4 mb-2">
                        <div className="flex items-center gap-2">
                          <User className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm font-medium">{booking.passenger_name}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">{booking.passenger_phone}</span>
                        <span className="text-xs capitalize text-muted-foreground">{booking.vehicle_type?.replace(/_/g, ' ')}</span>
                        {booking.num_passengers && <span className="text-xs text-muted-foreground">{booking.num_passengers} pax</span>}
                        {booking.flight_number && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground"><Plane className="h-3 w-3" />{booking.flight_number}</span>
                        )}
                      </div>

                      <div className="flex items-start gap-4 text-sm">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                          <span className="truncate">{booking.pickup_location}</span>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                          <span className="truncate text-muted-foreground">{booking.dropoff_location}</span>
                        </div>
                      </div>
                    </div>

                    {/* Driver & Actions */}
                    <div className="flex items-center gap-3 lg:w-auto flex-shrink-0">
                      {booking.driver_name ? (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50">
                          <div className="w-7 h-7 rounded-full bg-[#C8A95B]/15 flex items-center justify-center">
                            <Truck className="h-3.5 w-3.5 text-[#C8A95B]" />
                          </div>
                          <span className="text-sm font-medium">{booking.driver_name}</span>
                        </div>
                      ) : booking.status !== "cancelled" && (
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-9 text-xs border-[#C8A95B]/30 text-[#C8A95B] hover:bg-[#C8A95B]/10"
                            onClick={() => setAutoAssignBooking(booking)}
                            data-testid={`auto-assign-${booking.id}`}
                          >
                            <Zap className="h-3.5 w-3.5 mr-1" />
                            Smart
                          </Button>
                          <Select onValueChange={(driverId) => handleDispatch(booking.id, driverId)}>
                            <SelectTrigger className="w-32 h-9 text-xs" data-testid={`dispatch-driver-${booking.id}`}>
                              <SelectValue placeholder="Manual" />
                            </SelectTrigger>
                            <SelectContent>
                              {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      {booking.status === "pending" && (
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => handleConfirm(booking.id)} data-testid={`confirm-trip-${booking.id}`}>
                          <CheckCircle className="h-3.5 w-3.5 mr-1" />Confirm
                        </Button>
                      )}

                      <Button size="sm" variant="ghost" className="h-9 w-9 p-0" onClick={() => navigate(`/booking/${booking.id}`)} data-testid={`view-trip-${booking.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Auto-Assign Dialog */}
      <AutoAssignDialog
        booking={autoAssignBooking}
        isOpen={!!autoAssignBooking}
        onClose={() => setAutoAssignBooking(null)}
        onAssigned={onRefresh}
      />
    </div>
  );
};

export default DispatchBoard;
